import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import {
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '../../../../../../../../web/components/MultipleSelect';
import { Button } from '../../../../../../../../web/components-v2/Button';
import { Modal } from '../../../../../../../../web/components/Modal';
import { type ComboboxItem, EditModeCombobox, EditModeMultipleSelect, EditModeToggle, useEditMode } from '../context';
import { getProviderConfig } from '../providers';
import { CORE_TAGS } from '../providers/constants';
import type { CustomTagSection, DynamicCustomTagSection, StaticCustomTagSection } from '../providers/types';
import {
	getAvailableFields,
	getConfigValue,
	getFieldsDisplayValue,
	getSettingValue,
	recordToItems,
} from '../providers/utils';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';
import { SettingsFieldRenderer } from './SettingsFieldRenderer';

interface ITagSettingsGroupProps {
	tagSettings: Api.ITagSettings;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setTagsSettings: (settings: Api.ITagSettings) => void;
}

export const TagSettingsGroup: React.FC<ITagSettingsGroupProps> = ({
	tagSettings,
	configuration,
	provider,
	setTagsSettings,
}) => {
	const providerConfig = getProviderConfig(provider);
	const tagsSchema = providerConfig.tags;
	const { isEditing } = useEditMode();
	const [managingDynamicSection, setManagingDynamicSection] = React.useState<string | null>(null);

	const onUpdate = (updatedSettings: Partial<Api.ITagSettings>) => {
		setTagsSettings({
			...tagSettings,
			...updatedSettings,
		});
	};

	// Helper to get enabled tags as array using provider config
	const getEnabledTags = (): string[] => {
		const enabledTags: string[] = [];
		if (!tagSettings) return enabledTags;

		// Check core tags if enabled for this provider
		if (tagsSchema?.showCoreTags) {
			CORE_TAGS.forEach(tag => {
				const value = getSettingValue<boolean>(tagSettings, tag.settingsKey);
				if (value) enabledTags.push(tag.id);
			});
		}

		// Check provider-specific tags from config
		if (tagsSchema?.providerTags) {
			tagsSchema.providerTags.forEach(tag => {
				const value = getSettingValue<boolean>(tagSettings, tag.settingsKey);
				if (value) enabledTags.push(tag.id);
			});
		}

		// Check dynamic tags from config (e.g., Salesforce enabledFields)
		if (tagsSchema?.dynamicTags) {
			const { settingsKey } = tagsSchema.dynamicTags;
			const dynamicTagIds = getSettingValue<string[]>(tagSettings, settingsKey) ?? [];
			enabledTags.push(...dynamicTagIds);
		}

		return enabledTags;
	};

	// Handle tag selection changes using provider config
	const handleTagChange = (selectedTags: string[]) => {
		const updatedSettings: Record<string, unknown> = {};

		// Update core tags if enabled for this provider
		if (tagsSchema?.showCoreTags) {
			CORE_TAGS.forEach(tag => {
				updatedSettings[tag.settingsKey] = selectedTags.includes(tag.id);
			});
		}

		// Update provider-specific tags from config
		if (tagsSchema?.providerTags) {
			tagsSchema.providerTags.forEach(tag => {
				updatedSettings[tag.settingsKey] = selectedTags.includes(tag.id);
			});
		}

		// Update dynamic tags from config (e.g., Salesforce enabledFields)
		if (tagsSchema?.dynamicTags) {
			const { optionsFrom, settingsKey } = tagsSchema.dynamicTags;
			const availableFieldKeys = Object.keys(getConfigValue<Record<string, string>>(configuration, optionsFrom) ?? {});
			const enabledFields = selectedTags.filter(tag => availableFieldKeys.includes(tag));
			updatedSettings[settingsKey] = enabledFields;
		}

		onUpdate(updatedSettings as Partial<Api.ITagSettings>);
	};

	// Get tag options based on provider config
	const getTagOptions = (): ComboboxItem[] => {
		const options: ComboboxItem[] = [];

		// Add core location tags if enabled by config
		if (tagsSchema?.showCoreTags) {
			options.push(...CORE_TAGS.map(t => ({ id: t.id, label: t.label })));
		}

		// Add provider-specific tags from config
		if (tagsSchema?.providerTags) {
			options.push(...tagsSchema.providerTags.map(t => ({ id: t.id, label: t.label })));
		}

		// Add dynamic tags from configuration (e.g., Salesforce availableFields)
		if (tagsSchema?.dynamicTags) {
			const { optionsFrom } = tagsSchema.dynamicTags;
			const dynamicOptions = getConfigValue<Record<string, string>>(configuration, optionsFrom);
			if (dynamicOptions) {
				options.push(...recordToItems(dynamicOptions));
			}
		}

		return options;
	};

	// Get display value for enabled tags
	const getTagOptionsDisplay = (): string => {
		const enabledTags = getEnabledTags();
		if (!enabledTags.length) return 'None selected';

		const tagOptions = getTagOptions();
		return enabledTags.map(tagId => tagOptions.find(opt => opt.id === tagId)?.label || tagId).join(', ');
	};

	// Render custom fields section if configured
	const renderCustomFields = () => {
		if (!tagsSchema?.customFields) return null;

		const { availableFrom, settingsKey } = tagsSchema.customFields;
		const availableFields = getAvailableFields(configuration, availableFrom);
		const enabledFields = getSettingValue<string[]>(tagSettings, settingsKey) ?? [];

		if (!availableFields || !Object.keys(availableFields).length) return null;

		return (
			<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
				<EditModeCombobox
					multiple
					items={recordToItems(availableFields)}
					value={enabledFields}
					onChange={(selectedFields: string[]) => onUpdate({ [settingsKey]: selectedFields })}
					displayValue={getFieldsDisplayValue(enabledFields, availableFields)}
					searchPlaceholder='Search custom fields...'
				/>
			</SettingRow>
		);
	};

	// Get enabled values for a static custom section based on its storage type
	const getStaticSectionEnabledValues = (section: StaticCustomTagSection): string[] => {
		if (section.storageType === 'nested') {
			const nestedSettings = getSettingValue<Record<string, boolean>>(tagSettings, section.settingsKey);
			if (!nestedSettings) return [];
			return section.options.filter(opt => nestedSettings[opt.id]).map(opt => opt.label);
		} else {
			return getSettingValue<string[]>(tagSettings, section.settingsKey) ?? [];
		}
	};

	// Handle static custom section value changes
	const handleStaticSectionChange = (section: StaticCustomTagSection, selectedLabels: string[]) => {
		if (section.storageType === 'nested') {
			const nestedSettings: Record<string, boolean> = {};
			section.options.forEach(opt => {
				nestedSettings[opt.id] = selectedLabels.includes(opt.label);
			});
			onUpdate({ [section.settingsKey]: nestedSettings } as Partial<Api.ITagSettings>);
		} else {
			onUpdate({ [section.settingsKey]: selectedLabels } as Partial<Api.ITagSettings>);
		}
	};

	// Render a static custom tag section (e.g., Campaign Tags)
	const renderStaticCustomSection = (section: StaticCustomTagSection) => {
		const enabledValues = getStaticSectionEnabledValues(section);
		const displayValue = enabledValues.length > 0 ? enabledValues.join(', ') : 'None selected';

		return (
			<SettingRow key={section.id} title={section.label} subtitle={section.subtitle}>
				<EditModeMultipleSelect
					value={enabledValues}
					onChange={(selected: string[]) => handleStaticSectionChange(section, selected)}
					displayValue={displayValue}
				>
					<MultipleSelectTrigger styles={styleSheet.editableField}>
						<MultipleSelectValue values={enabledValues} placeholder={`Select ${section.label.toLowerCase()}`} />
					</MultipleSelectTrigger>
					<MultipleSelectContent>
						{section.options.map(opt => (
							<MultipleSelectItem key={opt.id} value={opt.label} selected={enabledValues.includes(opt.label)}>
								{opt.label}
							</MultipleSelectItem>
						))}
					</MultipleSelectContent>
				</EditModeMultipleSelect>
			</SettingRow>
		);
	};

	// Convert dynamic section config data to ComboboxItems
	const getDynamicSectionItems = (section: DynamicCustomTagSection): ComboboxItem[] => {
		const { optionsFrom, idKey, labelKey, descriptionKey } = section;
		const availableOptions = getConfigValue<Record<string, unknown>[]>(configuration, optionsFrom);
		if (!availableOptions?.length) return [];

		return availableOptions.map(opt => {
			const descValue = descriptionKey ? opt[descriptionKey] : undefined;
			const description = Array.isArray(descValue) ? descValue.join(', ') : (descValue as string | undefined);
			return {
				id: opt[idKey] as string,
				label: opt[labelKey] as string,
				description: description || undefined,
			};
		});
	};

	// Render a dynamic custom tag section with toggle + manage button (exclusion model)
	const renderDynamicToggleSection = (section: DynamicCustomTagSection) => {
		const { settingsKey, enabledKey, enabledToggleKey = 'enabled', manageButtonLabel = 'Manage' } = section;
		const items = getDynamicSectionItems(section);

		// Hide if no available options
		if (!items.length) return null;

		const nestedSettings = getSettingValue<Record<string, unknown>>(tagSettings, settingsKey);
		const isEnabled = Boolean(nestedSettings?.[enabledToggleKey]);
		const excludedIds = (nestedSettings?.[enabledKey] as string[]) ?? [];

		const handleToggle = (checked: boolean) => {
			onUpdate({
				[settingsKey]: {
					...nestedSettings,
					[enabledToggleKey]: checked,
				},
			} as Partial<Api.ITagSettings>);
		};

		const handleExclusionChange = (selectedExcludedIds: string[]) => {
			onUpdate({
				[settingsKey]: {
					...nestedSettings,
					[enabledKey]: selectedExcludedIds,
				},
			} as Partial<Api.ITagSettings>);
		};

		const excludedNames = excludedIds.map(id => items.find(item => item.id === id)?.label).filter(Boolean);
		const excludedDisplay = excludedNames.length > 0 ? excludedNames.join(', ') : 'None excluded';

		// Build display value for the toggle row
		let displayValue: string | undefined;
		if (isEnabled) {
			displayValue =
				excludedNames.length > 0 ? `Enabled - Excluding ${excludedNames.join(', ')}` : 'Enabled - All tags active';
		}

		const modalTitleId = `modal-title-${section.id}`;

		return (
			<React.Fragment key={section.id}>
				<SettingRow title={section.label} subtitle={section.subtitle}>
					{isEnabled && isEditing ? (
						<Button variant='primary' onClick={() => setManagingDynamicSection(section.id)}>
							{manageButtonLabel}
						</Button>
					) : null}
					<EditModeToggle
						id={section.id}
						isOn={isEnabled}
						onToggleCheckChanged={handleToggle}
						displayValue={displayValue}
					/>
				</SettingRow>
				<Modal
					isOpen={managingDynamicSection === section.id}
					onRequestClose={() => setManagingDynamicSection(null)}
					useDefaultHeader
				>
					<div className={css(styleSheet.dynamicSectionModalContent)} role='dialog' aria-labelledby={modalTitleId}>
						<h3 id={modalTitleId} className={css(styleSheet.dynamicSectionModalTitle)}>
							Exclude {section.label}
						</h3>
						<p className={css(styleSheet.dynamicSectionModalSubtitle)}>
							Select tags to exclude. Excluded tags will not be synced to contacts.
						</p>
						<EditModeCombobox
							multiple
							items={items}
							value={excludedIds}
							onChange={handleExclusionChange}
							displayValue={excludedDisplay}
							searchPlaceholder={`Search ${section.label.toLowerCase()}...`}
						/>
						<div className={css(styleSheet.dynamicSectionModalActions)}>
							<Button variant='primary' onClick={() => setManagingDynamicSection(null)}>
								Done
							</Button>
						</div>
					</div>
				</Modal>
			</React.Fragment>
		);
	};

	// Render a dynamic custom tag section with simple inclusion model
	const renderDynamicInclusionSection = (section: DynamicCustomTagSection) => {
		const { settingsKey, enabledKey } = section;
		const items = getDynamicSectionItems(section);

		if (!items.length) return null;

		const nestedSettings = getSettingValue<Record<string, unknown>>(tagSettings, settingsKey);
		const enabledIds = (nestedSettings?.[enabledKey] as string[]) ?? [];

		const selectedNames = enabledIds.map(id => items.find(item => item.id === id)?.label).filter(Boolean);
		const displayValue = selectedNames.length > 0 ? selectedNames.join(', ') : 'None selected';

		return (
			<SettingRow key={section.id} title={section.label} subtitle={section.subtitle}>
				<EditModeCombobox
					multiple
					items={items}
					value={enabledIds}
					onChange={(selectedIds: string[]) =>
						onUpdate({
							[settingsKey]: {
								...nestedSettings,
								[enabledKey]: selectedIds,
							},
						} as Partial<Api.ITagSettings>)
					}
					displayValue={displayValue}
					searchPlaceholder={`Search ${section.label.toLowerCase()}...`}
				/>
			</SettingRow>
		);
	};

	// Render a dynamic custom tag section, dispatching by model type
	const renderDynamicCustomSection = (section: DynamicCustomTagSection) => {
		if (section.enableToggle) {
			return renderDynamicToggleSection(section);
		}
		return renderDynamicInclusionSection(section);
	};

	// Render a custom tag section, dispatching by storage type
	const renderCustomSection = (section: CustomTagSection) => {
		if (section.storageType === 'dynamic') {
			return renderDynamicCustomSection(section);
		}
		return renderStaticCustomSection(section);
	};

	return (
		<SettingGroup title='Tag Settings'>
			<SettingRow title='Tags' subtitle='Select taggable information to associate with contacts'>
				<EditModeCombobox
					multiple
					items={getTagOptions()}
					value={getEnabledTags()}
					onChange={handleTagChange}
					displayValue={getTagOptionsDisplay()}
					searchPlaceholder='Search tags...'
				/>
			</SettingRow>

			{renderCustomFields()}
			{tagsSchema?.extraFields?.map(field => (
				<SettingsFieldRenderer
					key={field.key}
					field={field}
					settings={tagSettings}
					onUpdate={updates => onUpdate(updates as Partial<Api.ITagSettings>)}
					configuration={configuration}
				/>
			))}
			{tagsSchema?.customSections?.map(section => renderCustomSection(section))}
		</SettingGroup>
	);
};
